import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, startOfToday, startOfWeek, startOfMonth, endOfToday, endOfWeek, endOfMonth, startOfYesterday, endOfYesterday } from "date-fns";
import { cn } from "@/lib/utils";

export type DateFilterType = "all" | "today" | "yesterday" | "week" | "month" | "custom";

export interface DateFilterValue {
  type: DateFilterType;
  startDate?: Date;
  endDate?: Date;
}

interface IntakeDateFilterProps {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
}

export function IntakeDateFilter({ value, onChange }: IntakeDateFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customRange, setCustomRange] = useState<{
    from?: Date;
    to?: Date;
  }>({
    from: value.startDate,
    to: value.endDate,
  });

  const getDateRangeLabel = () => {
    switch (value.type) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "week":
        return "This Week";
      case "month":
        return "This Month";
      case "custom":
        if (value.startDate && value.endDate) {
          return `${format(value.startDate, "MMM d")} - ${format(value.endDate, "MMM d, yyyy")}`;
        }
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  const handleQuickFilter = (type: DateFilterType) => {
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (type) {
      case "today":
        startDate = startOfToday();
        endDate = endOfToday();
        break;
      case "yesterday":
        startDate = startOfYesterday();
        endDate = endOfYesterday();
        break;
      case "week":
        startDate = startOfWeek(new Date(), { weekStartsOn: 0 });
        endDate = endOfWeek(new Date(), { weekStartsOn: 0 });
        break;
      case "month":
        startDate = startOfMonth(new Date());
        endDate = endOfMonth(new Date());
        break;
      case "all":
        startDate = undefined;
        endDate = undefined;
        break;
    }

    onChange({ type, startDate, endDate });
    if (type !== "custom") {
      setIsOpen(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (customRange.from && customRange.to) {
      onChange({
        type: "custom",
        startDate: customRange.from,
        endDate: customRange.to,
      });
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal",
            !value.startDate && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDateRangeLabel()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 space-y-2 border-b">
          <Button
            variant={value.type === "all" ? "default" : "ghost"}
            className="w-full justify-start"
            size="sm"
            onClick={() => handleQuickFilter("all")}
          >
            All Time
          </Button>
          <Button
            variant={value.type === "today" ? "default" : "ghost"}
            className="w-full justify-start"
            size="sm"
            onClick={() => handleQuickFilter("today")}
          >
            Today
          </Button>
          <Button
            variant={value.type === "yesterday" ? "default" : "ghost"}
            className="w-full justify-start"
            size="sm"
            onClick={() => handleQuickFilter("yesterday")}
          >
            Yesterday
          </Button>
          <Button
            variant={value.type === "week" ? "default" : "ghost"}
            className="w-full justify-start"
            size="sm"
            onClick={() => handleQuickFilter("week")}
          >
            This Week
          </Button>
          <Button
            variant={value.type === "month" ? "default" : "ghost"}
            className="w-full justify-start"
            size="sm"
            onClick={() => handleQuickFilter("month")}
          >
            This Month
          </Button>
        </div>
        <div className="p-3">
          <div className="text-sm font-medium mb-2">Custom Date Range</div>
          <Calendar
            mode="range"
            selected={{
              from: customRange.from,
              to: customRange.to,
            }}
            onSelect={(range) => {
              if (range) {
                setCustomRange({
                  from: range.from,
                  to: range.to,
                });
              }
            }}
            numberOfMonths={2}
          />
          <Button
            className="w-full mt-3"
            size="sm"
            onClick={handleCustomRangeApply}
            disabled={!customRange.from || !customRange.to}
          >
            Apply Custom Range
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
